import React, { FC, useMemo, ChangeEvent, useCallback } from "react";

import CheckboxGroup from "../../Unknown/CheckboxGroup";

import { AddOn, AddOnsState, CenterConfig, CenterServicesState } from "./types";
import useTranslations from "./translations";
import parseAddOn from "./parseAddOn";
import { calcAddOnPrice } from "./calcAddOnPrice";

export interface SelectAddOnsFormProps {
  addOns: AddOn[] | null;
  centerConfig: CenterConfig;
  selectedAddOns: AddOnsState;
  isSubmitting: boolean;
  selectedCenterServices: CenterServicesState;
  onAddOnsChange: React.Dispatch<React.SetStateAction<AddOnsState>>;
  isStatAvailableForAppointmentTime: boolean;
  isStatLocked?: boolean;
}

const SelectAddOnsForm: FC<SelectAddOnsFormProps> = ({
  addOns,
  centerConfig,
  selectedAddOns,
  isSubmitting,
  selectedCenterServices,
  onAddOnsChange,
  isStatAvailableForAppointmentTime,
  isStatLocked = false,
}) => {
  const { translations } = useTranslations();

  const selectedStudiesCount = useMemo(() => {
    return selectedCenterServices.reduce((count, curr) => {
      return count + (curr?.studyIds?.length || 0);
    }, 0);
  }, [selectedCenterServices]);

  const onQuantityChange = useCallback(
    (addOnId: string, quantity: number): void => {
      onAddOnsChange((prev) =>
        prev.map((selected) => {
          if (selected.id !== addOnId) return selected;

          const price = calcAddOnPrice({
            addOnPrice: selected.unitPrice,
            isPerStudyPricing: selected.isPerStudyPricing,
            selectedStudiesCount,
            quantity,
          });

          return { ...selected, quantity, price };
        }),
      );
    },
    [onAddOnsChange, selectedStudiesCount],
  );

  const options = useMemo(() => {
    if (!centerConfig || !addOns?.length) return [];

    const { defaultCurrencyCode } = centerConfig;

    return addOns
      .filter((addon) => addon.appliesTo === "appointment")
      .filter(
        (addon) =>
          addon.type !== "stat" ||
          isStatLocked ||
          isStatAvailableForAppointmentTime,
      )
      .map((addOn) => {
        const selected = selectedAddOns.find(
          (selectedAddOn) => selectedAddOn.id === addOn.id,
        );
        const checked = !!selected;
        const quantity = selected?.quantity ?? 1;

        const studyAdjustedPrice = addOn.isPerStudyPricing
          ? addOn.price * Math.max(selectedStudiesCount, 1)
          : addOn.price;

        return parseAddOn({
          addOn: { ...addOn, price: studyAdjustedPrice },
          currencyCode: defaultCurrencyCode,
          checked,
          quantity,
          disabled: isStatLocked && addOn.type === "stat",
          onQuantityChange: addOn.allowMultiplePurchases
            ? (newQuantity: number) => onQuantityChange(addOn.id, newQuantity)
            : undefined,
        });
      });
  }, [
    addOns,
    centerConfig,
    selectedAddOns,
    selectedStudiesCount,
    isStatAvailableForAppointmentTime,
    isStatLocked,
    onQuantityChange,
  ]);

  const onChecked = useCallback(
    (checkedValue?: string): void => {
      if (!centerConfig || !addOns?.length) return;

      if (!checkedValue) return;

      const selectedAddOn = addOns.find((addOn) => addOn.id === checkedValue);

      if (!selectedAddOn) return;

      const price = calcAddOnPrice({
        addOnPrice: selectedAddOn.price,
        isPerStudyPricing: selectedAddOn.isPerStudyPricing,
        selectedStudiesCount,
      });

      onAddOnsChange((prev) => [
        ...prev,
        { ...selectedAddOn, price, quantity: 1 },
      ]);
    },
    [addOns, centerConfig, onAddOnsChange, selectedStudiesCount],
  );

  const onUnchecked = useCallback(
    (uncheckedValue?: string) => {
      if (!uncheckedValue) return;

      const indexOfValue = selectedAddOns.findIndex(
        (addOn) => addOn.id === uncheckedValue,
      );

      const isValueInSelectedItems = indexOfValue !== -1;

      if (!isValueInSelectedItems) return;

      onAddOnsChange((prev) => {
        const newAddOns = [...prev];
        newAddOns.splice(indexOfValue, 1);
        return newAddOns;
      });
    },
    [onAddOnsChange, selectedAddOns],
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      const actionFunc = checked ? onChecked : onUnchecked;
      actionFunc(e?.target?.value);
    },
    [onChecked, onUnchecked],
  );

  if (!centerConfig || !addOns?.length) return null;

  return (
    <CheckboxGroup
      title={translations.selectAdOnsTitle}
      options={options}
      onChange={onChange}
      disabled={isSubmitting}
    />
  );
};

export default SelectAddOnsForm;
